import { useEffect, useState, useRef } from 'react';
import { siteConfig } from '@/config/site';
import './BrandWatermark.css';

/**
 * Casa de Candy floating brand watermark.
 *
 * Renders a fixed-position logo behind the page content. Becomes visible
 * once the user scrolls past the trigger element (passed via `triggerSelector`).
 * The logo keeps its primary glow + breathing animation; its borders fade
 * radially into the background colour for a premium dynamic feel.
 */
export function BrandWatermark({ triggerSelector = '#about' }) {
  const [visible, setVisible] = useState(false);
  const triggerRef = useRef(null);

  useEffect(() => {
    const target = document.querySelector(triggerSelector);
    if (!target) return;
    triggerRef.current = target;

    // Reveal once the trigger section's bottom border passes the viewport top.
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const rect = entry.boundingClientRect;
          // bottom of trigger above viewport top => user scrolled past it
          setVisible(rect.bottom < window.innerHeight * 0.4);
        });
      },
      {
        // Fire continuously as the user scrolls through and past the trigger
        threshold: [0, 0.25, 0.5, 0.75, 1],
      }
    );
    observer.observe(target);

    // Backup scroll listener — IntersectionObserver may not refire when the
    // section is fully out of view (no entries beyond the last threshold).
    const onScroll = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      setVisible(rect.bottom < window.innerHeight * 0.4);
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
    };
  }, [triggerSelector]);

  return (
    <div
      className={`brand-watermark ${visible ? 'is-visible' : ''}`}
      aria-hidden
      data-testid="brand-watermark"
    >
      <div className="brand-watermark__halo" />
      <img
        src={siteConfig.brand.logoUrl}
        alt=""
        className="brand-watermark__logo animate-breathe"
      />
    </div>
  );
}

export default BrandWatermark;
